import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import { auth, db } from '../firebaseConfig';
import { doc, updateDoc } from 'firebase/firestore';
import { trackFunnelEvent } from '../utils/AnalyticsService';
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { trackGA4Event, getGAClientId } from '../utils/GA4Analytics';

export default function PricingPage() {
    const navigate = useNavigate();
    const [isYearly, setIsYearly] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
    const [currency, setCurrency] = useState('INR');

    const locale = sessionStorage.getItem('versant_locale') || 'en';

    const pricingTranslations = {
        en: {
            title: "Simple, Transparent Pricing",
            subtitle: "Select the option that matches your preparation needs. Upgrade to unlock mock tests and deep analytics.",
            freeTitle: "Free Trial",
            freeDesc: "Perfect for testing our AI engine before committing.",
            freeButton: "Current Plan",
            sectionTitle: "Section Packs",
            sectionDesc: "Perfect if you need to improve one specific section.",
            sectionButton: "Practice This Section →",
            proTitle: "Versant Pro Pass",
            proDesc: "Everything you need to pass Versant English Tests & cross MNC screening cutoffs.",
            proButton: "Upgrade to Pro",
            fomoTitle: "🔥 FLASH SALE ENDING...",
            practiceTitle: "Target Your Weakest Sections",
            practiceSubtitle: "Don't pay for the full pass if you only struggle in one area. Unlock 20 targeted practice sets with AI feedback for a single section.",
            days: "Days",
            hrs: "Hrs",
            mins: "Mins",
            secs: "Secs",
            perSection: "per section",
            onetime: "One-time payment",
            popular: "MOST POPULAR",
            lifetime: "LIFETIME ACCESS (Usually $29.99/mo)",
            offerTag: "Special Limited Offer",
            mainTitle: "Choose Your Versant Preparation Plan",
            mainSub: "Practice smarter. Improve your score. Be ready for your actual test.",
            mainDesc: "Select the option that matches your preparation needs. Upgrade to unlock all mock tests and deep analytics.",
            secured: "256-bit Secured by Razorpay",
            rating: "4.8/5 Student Rating",
            lifetimeAccess: "Lifetime Access — No Renewals",
            refund: "7-Day Money Back Guarantee",
            trusted: "Trusted by candidates hired at",
            proFeatures: [
                "Full Access to 38 Premium Mock Exams (19 Reading/Writing + 19 Speaking)",
                "Unlimited AI Voice Evaluation & Scoring",
                "Deep Pronunciation & Grammar Corrections",
                "Model 'Perfect Answers' for every question",
                "No monthly subscriptions. Pay once.",
                "Instant 24/7 Digital Access"
            ]
        },
        es: {
            title: "Precios Simples y Transparentes",
            subtitle: "Selecciona la opción que se adapte a tus necesidades de preparación. Mejora para desbloquear simulacros y análisis profundos.",
            freeTitle: "Prueba Gratis",
            freeDesc: "Perfecto para probar nuestro motor de IA antes de comprometerse.",
            freeButton: "Plan Actual",
            sectionTitle: "Paquetes de Sección",
            sectionDesc: "Perfecto para candidatos que solo tienen dificultades en un área de evaluación específica.",
            sectionButton: "Selecciona una Sección Abajo",
            proTitle: "Pase Versant Pro",
            proDesc: "Todo lo que necesitas para aprobar las pruebas Versant y superar los filtros de selección.",
            proButton: "Mejorar a Pro",
            fomoTitle: "🔥 LA OFERTA TERMINA EN...",
            practiceTitle: "Apunta a tus Secciones Más Débiles",
            practiceSubtitle: "No pagues por el pase completo si solo tienes problemas en un área. Desbloquea 20 conjuntos de práctica específicos con feedback por IA.",
            days: "Días",
            hrs: "Hrs",
            mins: "Mins",
            secs: "Segs",
            perSection: "por sección",
            onetime: "Pago único",
            popular: "MÁS POPULAR",
            lifetime: "ACCESO DE POR VIDA (Normalmente €29.99/mes)",
            offerTag: "Oferta Especial Limitada",
            mainTitle: "Corrige tus Debilidades.",
            mainSub: "Pasa tu Entrevista.",
            mainDesc: "Tu IA ha identificado errores críticos. No dejes que te cuesten el trabajo de tus sueños. Desbloquea 19 pruebas premium con análisis profundo de pronunciación.",
            secured: "Seguridad de 256 bits por Razorpay",
            rating: "Calificación de 4.8/5 de estudiantes",
            lifetimeAccess: "Acceso de por vida — Sin renovaciones",
            refund: "Garantía de reembolso de 7 días",
            trusted: "Con la confianza de candidatos contratados en",
            proFeatures: [
                "Acceso completo a 19 exámenes de práctica premium",
                "Evaluación y calificación de voz por IA ilimitada",
                "Correcciones profundas de pronunciación y gramática",
                "Respuestas de muestra perfectas para cada pregunta",
                "Sin suscripciones mensuales. Paga una vez.",
                "Acceso digital instantáneo las 24 horas, los 7 días de la semana"
            ]
        },
        pt: {
            title: "Preços Simples e Transparentes",
            subtitle: "Selecione a opção que melhor se adapta à sua preparação. Faça o upgrade para desbloquear simulados e gráficos.",
            freeTitle: "Teste Grátis",
            freeDesc: "Perfeito para testar nossa tecnologia de IA antes de comprar.",
            freeButton: "Plano Atual",
            sectionTitle: "Pacotes de Seção",
            sectionDesc: "Perfeito para candidatos que precisam de foco em apenas uma área específica.",
            sectionButton: "Selecione a Seção Abaixo",
            proTitle: "Passe Versant Pro",
            proDesc: "Tudo o que você precisa para passar nos testes Versant e superar a triagem das multinacionais.",
            proButton: "Melhorar para Pro",
            fomoTitle: "🔥 A OFERTA TERMINA EM...",
            practiceTitle: "Foque nas suas Seções Mais Fracas",
            practiceSubtitle: "Não pague pelo passe completo se você só tem dificuldade em uma área. Desbloqueie 20 simulados focados com avaliação de IA.",
            days: "Dias",
            hrs: "Hrs",
            mins: "Mins",
            secs: "Segs",
            perSection: "por seção",
            onetime: "Pagamento único",
            popular: "MAIS POPULAR",
            lifetime: "ACESSO VITALÍCIO (Normalmente $29.99/mês)",
            offerTag: "Oferta Especial Limitada",
            mainTitle: "Corrija suas Fraquezas.",
            mainSub: "Passe na sua Entrevista.",
            mainDesc: "Sua IA identificou erros críticos. Não deixe que eles custem o emprego dos seus sonhos. Desbloqueie 19 testes premium com análise de pronúncia.",
            secured: "Segurança de 256 bits por Razorpay",
            rating: "Avaliação de 4.8/5 por alunos",
            lifetimeAccess: "Acesso vitalício — Sem assinaturas",
            refund: "Garantia de reembolso de 7 dias",
            trusted: "Confiado por candidatos contratados na",
            proFeatures: [
                "Acesso completo a 19 simulados de teste premium",
                "Avaliação e pontuação de voz por IA ilimitadas",
                "Correções profundas de pronúncia e gramática",
                "Respostas perfeitas de exemplo para cada pergunta",
                "Sem assinatura mensal. Pague uma única vez.",
                "Acesso digital instantâneo 24 horas por dia, 7 dias por semana"
            ]
        },
        ja: {
            title: "シンプルで透明な料金プラン",
            subtitle: "あなたの準備状況に合わせたプランをお選びください。アップグレードして、すべての本番模試と詳細分析をアンロックしましょう。",
            freeTitle: "無料プラン",
            freeDesc: "購入前にAIエンジンの精度をテストするのに最適です。",
            freeButton: "現在のプラン",
            sectionTitle: "セクション個別パック",
            sectionDesc: "特定の苦手なセクションだけを集中して対策したい方に最適です。",
            sectionButton: "以下からセクションを選択",
            proTitle: "Versant Pro パス",
            proDesc: "企業面接や採用テストを突破するために必要なすべてが含まれています。",
            proButton: "Proにアップグレード",
            fomoTitle: "🔥 特別セール終了まで...",
            practiceTitle: "苦手なセクションに集中対策",
            practiceSubtitle: "一部分だけが苦手な場合、フルパスを購入する必要はありません。AIによる自動判定フィードバック付きの20回分セクション別パックをアンロックしましょう。",
            days: "日",
            hrs: "時間",
            mins: "分",
            secs: "秒",
            perSection: "1セクションあたり",
            onetime: "一括支払い",
            popular: "一番人気",
            lifetime: "無期限アクセス可能（通常 月額 $29.99）",
            offerTag: "期間限定特別オファー",
            mainTitle: "弱点を克服し、",
            mainSub: "面接を一発でクリアする。",
            mainDesc: "AIが重要な改善ポイントを検出しました。第一志望の採用を諦めないでください。19個のプレミアム模擬試験と詳細な発音分析をアンロックして合格を確実にしましょう。",
            secured: "Razorpayによる256ビット安全決済",
            rating: "受講生評価 4.8/5",
            lifetimeAccess: "無期限アクセス — 更新費用なし",
            refund: "7日間の返金保証",
            trusted: "大手企業の内定獲得実績",
            proFeatures: [
                "19回分の本番形式プレミアム模試へのアクセス",
                "無制限のAI音声評価・採点機能",
                "詳細な発音と文法の修正フィードバック",
                "すべての問題に対する模範解答（合格サンプル）",
                "月額課金なし。一括支払いの買い切りプラン。",
                "24時間いつでも即時にデジタルアクセス可能"
            ]
        }
    };

    const pt = pricingTranslations[locale] || pricingTranslations.en;

    const getLocalizedPacks = () => {
        const defaultPacks = [
            { id: 'repeats', name: 'Repeat Sentence Mastery', icon: '🎙️', desc: 'Unlock 20 full Repeat Sentence practice sets + AI voice & fluency evaluation.' },
            { id: 'storyRetelling', name: 'Story Retelling Pro', icon: '🎧', desc: 'Unlock 20 detailed Story Retelling prompts + structured outline mapping.' },
            { id: 'emailWriting', name: 'Email Writing Mastery', icon: '✉️', desc: 'Unlock 20 Email Writing tasks with advanced grammar & structure checks.' },
            { id: 'passageReconstruction', name: 'Passage Reconstruction Pro', icon: '📝', desc: 'Unlock 20 Passage Reconstruction drills to train memory retention.' },
            { id: 'readAloud', name: 'Read Aloud Practice Pack', icon: '📖', desc: 'Unlock 20 Read Aloud sets to optimize pronunciation and pacing index.' },
            { id: 'fillBlanks', name: 'Dictation & Fill in Blanks', icon: '🧩', desc: 'Unlock 20 Fill in the Blanks tests to perfect speed-listening syntax.' },
            { id: 'shortAnswer', name: 'Short Answer Mastery', icon: '❓', desc: 'Unlock 20 Short Answer sets to practice instant word association scoring.' },
            { id: 'sentenceBuilds', name: 'Sentence Builds Pro', icon: '🧩', desc: 'Unlock 20 Sentence Builds practice sets to perfect syntax & phrase ordering.' },
            { id: 'openQuestions', name: 'Open Questions Mastery', icon: '💬', desc: 'Unlock 20 Open Questions sets with 40-sec speech flow analysis.' },
            { id: 'typing', name: 'Typing Test Pro', icon: '⌨️', desc: 'Unlock 20 Typing Tests to optimize your net speed (WPM) and accuracy score.' },
            { id: 'sentenceCompletion', name: 'Sentence Completion Pack', icon: '✍️', desc: 'Unlock 20 Sentence Completion sets to perfect grammatical matching.' },
            { id: 'jumbledWords', name: 'Jumbled Words Mastery', icon: '🔀', desc: 'Unlock 20 Jumbled Words sets to practice rapid sentence recognition.' }
        ];

        if (locale === 'es') {
            return [
                { id: 'repeats', name: 'Dominio de Repetición de Oraciones', icon: '🎙️', desc: 'Desbloquea 20 conjuntos completos de práctica + evaluación de voz y fluidez por IA.' },
                { id: 'storyRetelling', name: 'Reconstrucción de Historias Pro', icon: '🎧', desc: 'Desbloquea 20 temas detallados de reconstrucción de historias + mapeo de esquema.' },
                { id: 'emailWriting', name: 'Dominio de Escritura de Correos', icon: '✉️', desc: 'Desbloquea 20 tareas de escritura de correos con revisiones avanzadas de gramática.' },
                { id: 'passageReconstruction', name: 'Reconstrucción de Pasajes Pro', icon: '📝', desc: 'Desbloquea 20 ejercicios de reconstrucción para entrenar la retención de memoria.' },
                { id: 'readAloud', name: 'Paquete de Lectura en Voz Alta', icon: '📖', desc: 'Desbloquea 20 conjuntos de lectura para optimizar la pronunciación y el ritmo.' },
                { id: 'fillBlanks', name: 'Dictado y Rellenar Espacios', icon: '🧩', desc: 'Desbloquea 20 pruebas de rellenar espacios para perfeccionar la sintaxis auditiva.' },
                { id: 'shortAnswer', name: 'Dominio de Respuestas Cortas', icon: '❓', desc: 'Desbloquea 20 conjuntos de respuestas cortas para practicar la asociación de palabras.' },
                { id: 'sentenceBuilds', name: 'Construcción de Oraciones Pro', icon: '🧩', desc: 'Desbloquea 20 conjuntos de práctica para perfeccionar el orden de las frases.' },
                { id: 'openQuestions', name: 'Dominio de Preguntas Abiertas', icon: '💬', desc: 'Desbloquea 20 conjuntos con análisis de flujo de voz de 40 segundos.' },
                { id: 'typing', name: 'Prueba de Mecanografía Pro', icon: '⌨️', desc: 'Desbloquea 20 pruebas para optimizar tu velocidad neta (WPM) y precisión.' },
                { id: 'sentenceCompletion', name: 'Completar Oraciones Pack', icon: '✍️', desc: 'Desbloquea 20 conjuntos para perfeccionar la concordancia gramatical.' },
                { id: 'jumbledWords', name: 'Dominio de Palabras Desordenadas', icon: '🔀', desc: 'Desbloquea 20 conjuntos para practicar el reconocimiento rápido de oraciones.' }
            ];
        }
        if (locale === 'pt') {
            return [
                { id: 'repeats', name: 'Domínio de Repeat Sentence', icon: '🎙️', desc: 'Desbloqueie 20 simulados completos de Repeat Sentence + avaliação por IA.' },
                { id: 'storyRetelling', name: 'Story Retelling Pro', icon: '🎧', desc: 'Desbloqueie 20 enunciados detalhados de Story Retelling + mapeamento estruturado.' },
                { id: 'emailWriting', name: 'Domínio de Email Writing', icon: '✉️', desc: 'Desbloqueie 20 tarefas de Email Writing com verificações gramaticais.' },
                { id: 'passageReconstruction', name: 'Passage Reconstruction Pro', icon: '📝', desc: 'Desbloqueie 20 exercícios de reconstrução para treinar a memória.' },
                { id: 'readAloud', name: 'Prática de Read Aloud Pack', icon: '📖', desc: 'Desbloqueie 20 simulados de leitura para otimizar pronúncia e ritmo.' },
                { id: 'fillBlanks', name: 'Ditado & Preenchimento de Lacunas', icon: '🧩', desc: 'Desbloqueie 20 testes de preenchimento para aperfeiçoar a escuta.' },
                { id: 'shortAnswer', name: 'Domínio de Short Answer', icon: '❓', desc: 'Desbloqueie 20 conjuntos de respostas curtas para praticar associação de palavras.' },
                { id: 'sentenceBuilds', name: 'Sentence Builds Pro', icon: '🧩', desc: 'Desbloqueie 20 conjuntos para treinar ordem de frases e sintaxe.' },
                { id: 'openQuestions', name: 'Domínio de Open Questions', icon: '💬', desc: 'Desbloqueie 20 conjuntos com análise de fluxo de fala de 40 segundos.' },
                { id: 'typing', name: 'Teste de Digitação Pro', icon: '⌨️', desc: 'Desbloqueie 20 testes de digitação para otimizar velocidade (WPM) e precisão.' },
                { id: 'sentenceCompletion', name: 'Pacote de Sentence Completion', icon: '✍️', desc: 'Desbloqueie 20 conjuntos de completar frases para gramática perfeita.' },
                { id: 'jumbledWords', name: 'Domínio de Jumbled Words', icon: '🔀', desc: 'Desbloqueie 20 locais de palavras embaralhadas para reconhecimento rápido.' }
            ];
        }
        if (locale === 'ja') {
            return [
                { id: 'repeats', name: 'リピートセンテンス攻略', icon: '🎙️', desc: '20回分のリピートセンテンス練習セット ＋ AI音声・流暢さ自動評価をアンロック。' },
                { id: 'storyRetelling', name: 'ストーリーリテリング Pro', icon: '🎧', desc: '20回分のストーリーリテリング問題 ＋ 構成のアウトラインマッピング機能をアンロック。' },
                { id: 'emailWriting', name: 'Eメールライティング攻略', icon: '✉️', desc: '20回分のEメールライティング課題 ＋ 高度な文法・構成チェック機能をアンロック。' },
                { id: 'passageReconstruction', name: 'パッセージ再構築 Pro', icon: '📝', desc: '記憶保持力を鍛えるための20回分のパッセージ（文章）再構築ドリルをアンロック。' },
                { id: 'readAloud', name: '音読練習パック', icon: '📖', desc: '発音と発話ペースの指標を最適化するための20回分の音読セットをアンロック。' },
                { id: 'fillBlanks', name: 'ディクテーション・空欄補充', icon: '🧩', desc: 'スピードリスニングの構文理解を完璧にするための20回分の空欄補充テスト。' },
                { id: 'shortAnswer', name: '簡潔な質問への回答攻略', icon: '❓', desc: '瞬時の単語連想による得点アップを狙う20回分のショートアンサーセット。' },
                { id: 'sentenceBuilds', name: '文章構築（語句整序） Pro', icon: '🧩', desc: '構文と語句の並び替え順序を完璧にするための20回分の練習セットをアンロック。' },
                { id: 'openQuestions', name: '自由回答（意見陳述）攻略', icon: '💬', desc: '40秒の発話フロー分析を含む20回分のオープンクエスチョン（自由回答）セット。' },
                { id: 'typing', name: 'タイピングテスト Pro', icon: '⌨️', desc: 'ネット速度（WPM）と正確性スコアを最適化するための20回分のタイピングテスト。' },
                { id: 'sentenceCompletion', name: '文完成パック', icon: '✍️', desc: '文法的なマッチングを完璧にするための20回分のセンテンス完成セット。' },
                { id: 'jumbledWords', name: '文の並べ替え攻略', icon: '🔀', desc: '迅速な文認識を練習するための20回分のジャンプルドワードセット。' }
            ];
        }

        return defaultPacks;
    };

    useEffect(() => {
        const getDetectedCurrency = () => {
            const country = sessionStorage.getItem('ga4_user_country');
            const browserLang = navigator.language || '';
            const sessionLocale = sessionStorage.getItem('versant_locale');
            const path = window.location.pathname;
            
            // Check if page path is /es, or geo is Spain/Spanish language, or session locale is es
            if (path.startsWith('/es') || country === 'Spain' || browserLang.startsWith('es') || sessionLocale === 'es') {
                return 'EUR';
            }

            // Check if Japanese locale or page path /ja
            if (path.startsWith('/ja') || country === 'Japan' || browserLang.startsWith('ja') || sessionLocale === 'ja') {
                return 'USD';
            }

            // Check if Portuguese locale or page path /pt
            if (path.startsWith('/pt') || country === 'Brazil' || country === 'Portugal' || browserLang.startsWith('pt') || sessionLocale === 'pt') {
                return 'USD';
            }
            
            // High paying markets
            const highPayingUSDCounteries = ['United States', 'United Kingdom', 'Canada', 'Singapore', 'Australia', 'South Korea'];
            if (highPayingUSDCounteries.includes(country)) {
                return 'USD';
            }
            
            if (country === 'India') {
                return 'INR';
            }
            
            // Default international fallback
            return 'USD';
        };

        setCurrency(getDetectedCurrency());
    }, []);

    useEffect(() => {
        const baseDate = new Date('May 25, 2026 23:59:59').getTime();
        const cycleDuration = 10 * 24 * 60 * 60 * 1000; // 10 days
        
        const interval = setInterval(() => {
            const now = new Date().getTime();
            let targetDate = baseDate;
            
            // Auto-extend by 10 days if sale date has passed
            if (now > baseDate) {
                const cyclesPassed = Math.floor((now - baseDate) / cycleDuration);
                targetDate = baseDate + (cyclesPassed + 1) * cycleDuration;
            }
            
            const distance = targetDate - now;

            if (distance < 0) {
                // Safeguard (should rarely hit since we recalculate targetDate)
                setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
                return;
            }

            setTimeLeft({
                days: Math.floor(distance / (1000 * 60 * 60 * 24)),
                hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
                minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
                seconds: Math.floor((distance % (1000 * 60)) / 1000)
            });
        }, 1000);

        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        trackGA4Event('premium_plan_view');
        if (window.gtag) {
            window.gtag('event', 'pricing_page_visit');
        }
        trackFunnelEvent('pricing_page_visit');
    }, []);

    // Initialize Razorpay dynamically
    const loadRazorpay = () => {
        return new Promise((resolve) => {
            const script = document.createElement('script');
            script.src = 'https://checkout.razorpay.com/v1/checkout.js';
            script.onload = () => resolve(true);
            script.onerror = () => resolve(false);
            document.body.appendChild(script);
        });
    };

    const handleUpgrade = async () => {
        trackGA4Event('premium_subscription_click', { product_name: 'VersantPro Premium Pass', payment_amount: 1449, currency: 'INR' });
        if (!auth.currentUser) {
            alert('Please sign in or create an account first to upgrade to Pro.');
            navigate('/signup');
            return;
        }

        // ✅ Capture uid NOW before any async popup that might lose auth context
        const currentUid = auth.currentUser.uid;
        const currentEmail = auth.currentUser.email || '';
        const currentName = auth.currentUser.displayName || 'User';

        setIsProcessing(true);
        const res = await loadRazorpay();

        if (!res) {
            alert('Razorpay failed to load. Are you offline or using a strict adblocker?');
            setIsProcessing(false);
            return;
        }

        // 1. Fetch Order ID from Secure Serverless API (Netlify)
        try {
            const orderResponse = await fetch('/.netlify/functions/createRazorpayOrder', { 
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    uid: currentUid,
                    email: currentEmail,
                    currency: currency,
                    productType: 'premium_pass',
                    referredBy: localStorage.getItem('versant_affiliate_ref') || null,
                    gaClientId: getGAClientId()
                })
            });
            if (!orderResponse.ok) {
                let errorDetails = orderResponse.statusText || 'Server Error (' + orderResponse.status + ')';
                try {
                    const errData = await orderResponse.json();
                    if (errData && errData.error) errorDetails = errData.error;
                } catch (e) {}
                throw new Error("Razorpay Order Error: " + errorDetails);
            }
            const orderData = await orderResponse.json();

            const options = {
                key: process.env.REACT_APP_RAZORPAY_KEY_ID,
                amount: orderData.amount,
                currency: orderData.currency,
                name: "VersantPro Premium",
                description: "Lifetime Access to 20 Mock Exams",
                image: "https://versantpro.com/logo.png",
                order_id: orderData.order_id,
                handler: async function (response) {
                    console.log("Payment Success! Initiating Server Verification...");
                    setIsProcessing(true);
                    
                    try {
                        const verifyRes = await fetch('/.netlify/functions/verifyRazorpayPayment', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                razorpay_order_id: response.razorpay_order_id,
                                razorpay_payment_id: response.razorpay_payment_id,
                                razorpay_signature: response.razorpay_signature,
                                uid: currentUid,
                                productType: 'premium_pass',
                                referredBy: localStorage.getItem('versant_affiliate_ref') || null
                            })
                        });

                        const verifyData = await verifyRes.json();

                        if (verifyRes.ok) {
                            trackGA4Event('razorpay_payment_success', {
                                transaction_id: response.razorpay_payment_id,
                                payment_amount: 1449,
                                currency: 'INR',
                                product_name: 'VersantPro Premium Pass',
                                payment_method: 'razorpay'
                            });
                            trackFunnelEvent('purchase', { value: 1449, currency: 'INR', transaction_id: response.razorpay_payment_id });
                            trackFunnelEvent('payment_success', {
                                value: 1449,
                                currency: 'INR',
                                product_name: 'VersantPro Premium Pass',
                                transaction_id: response.razorpay_payment_id
                            });
                            trackFunnelEvent('premium_activated');

                            alert("Payment Verified! Welcome to Versant Pro (Lifetime VIP). All tests are now unlocked.");
                            navigate('/dashboard');
                        } else {
                            alert("Payment verification failed: " + verifyData.error);
                            setIsProcessing(false);
                            trackFunnelEvent('payment_failed', { product_name: 'VersantPro Premium Pass', reason: verifyData.error || 'Verification failed' });
                        }
                    } catch (err) {
                        alert("Network error during verification. If money was deducted, contact support.");
                        console.error("Verification error:", err);
                        setIsProcessing(false);
                    }
                },
                prefill: {
                    name: currentName,
                    email: currentEmail,
                    contact: ""
                },
                theme: {
                    color: "#10b981" // Match Emerald Green UI
                },
                modal: {
                    ondismiss: function() {
                        trackGA4Event('razorpay_payment_failed', { product_name: 'VersantPro Premium Pass', payment_amount: 1449, currency: 'INR', reason: 'User dismissed checkout modal' });
                        trackFunnelEvent('payment_failed', { product_name: 'VersantPro Premium Pass', reason: 'User dismissed checkout modal' });
                    }
                }
            };

            trackGA4Event('razorpay_checkout_open', { product_name: 'VersantPro Premium Pass', payment_amount: 1449, currency: 'INR' });
            trackFunnelEvent('checkout_started', { product_name: 'VersantPro Premium Pass', payment_amount: 1449, currency: 'INR' });
            const paymentObject = new window.Razorpay(options);
            paymentObject.open();
        } catch (err) {
            console.error("Lifetime Pass payment initialization error:", err);
            alert(`Could not initialize payment: ${err.message || 'Please check your connection and try again.'}`);
        }
        setIsProcessing(false);
    };

    const handle45DayUpgrade = async () => {
        trackGA4Event('premium_subscription_click', { product_name: 'VersantPro 45-Day Pass', payment_amount: 699, currency: 'INR' });
        if (!auth.currentUser) {
            alert('Please sign in or create an account first to get the 45-Day Pass.');
            navigate('/signup');
            return;
        }

        const currentUid = auth.currentUser.uid;
        const currentEmail = auth.currentUser.email || '';
        const currentName = auth.currentUser.displayName || 'User';

        setIsProcessing(true);
        const res = await loadRazorpay();

        if (!res) {
            alert('Razorpay failed to load. Are you offline or using a strict adblocker?');
            setIsProcessing(false);
            return;
        }

        try {
            const orderResponse = await fetch('/.netlify/functions/createRazorpayOrder', { 
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    uid: currentUid,
                    email: currentEmail,
                    currency: currency,
                    productType: 'pass_45_days',
                    referredBy: localStorage.getItem('versant_affiliate_ref') || null,
                    gaClientId: getGAClientId()
                })
            });
            if (!orderResponse.ok) {
                let errorDetails = orderResponse.statusText || 'Server Error (' + orderResponse.status + ')';
                try {
                    const errData = await orderResponse.json();
                    if (errData && errData.error) errorDetails = errData.error;
                } catch (e) {}
                throw new Error("Razorpay Order Error: " + errorDetails);
            }
            const orderData = await orderResponse.json();

            const options = {
                key: process.env.REACT_APP_RAZORPAY_KEY_ID,
                amount: orderData.amount,
                currency: orderData.currency,
                name: "VersantPro 45-Day Pass",
                description: "45 Days Full Access to 38 Mock Exams",
                image: "https://versantpro.com/logo.png",
                order_id: orderData.order_id,
                handler: async function (response) {
                    setIsProcessing(true);
                    try {
                        const verifyRes = await fetch('/.netlify/functions/verifyRazorpayPayment', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                razorpay_order_id: response.razorpay_order_id,
                                razorpay_payment_id: response.razorpay_payment_id,
                                razorpay_signature: response.razorpay_signature,
                                uid: currentUid,
                                productType: 'pass_45_days',
                                referredBy: localStorage.getItem('versant_affiliate_ref') || null
                            })
                        });

                        const verifyData = await verifyRes.json();
                        if (verifyRes.ok) {
                            trackGA4Event('razorpay_payment_success', {
                                transaction_id: response.razorpay_payment_id,
                                payment_amount: 699,
                                currency: 'INR',
                                product_name: 'VersantPro 45-Day Pass',
                                payment_method: 'razorpay'
                            });
                            trackFunnelEvent('payment_success', { value: 699, currency: 'INR', product_name: 'VersantPro 45-Day Pass', transaction_id: response.razorpay_payment_id });
                            alert("Payment Verified! Welcome to Versant Pro (45-Day Pass). All tests are now unlocked for 45 days.");
                            navigate('/dashboard');
                        } else {
                            alert("Payment verification failed: " + verifyData.error);
                            setIsProcessing(false);
                        }
                    } catch (err) {
                        alert("Network error during verification.");
                        console.error("Verification error:", err);
                        setIsProcessing(false);
                    }
                },
                prefill: { name: currentName, email: currentEmail, contact: "" },
                theme: { color: "#10b981" }
            };

            const paymentObject = new window.Razorpay(options);
            paymentObject.open();
        } catch (err) {
            console.error("45-Day Pass payment initialization error:", err);
            alert(`Could not initialize payment: ${err.message || 'Please check your connection and try again.'}`);
        }
        setIsProcessing(false);
    };

    const handleSectionUpgrade = async (sectionId, sectionName) => {
        trackGA4Event('section_upgrade_click', { product_name: `VersantPro Section ${sectionName}`, payment_amount: 299, currency: 'INR' });
        if (!auth.currentUser) {
            alert('Please sign in or create an account first to unlock this section.');
            navigate('/signup');
            return;
        }

        const currentUid = auth.currentUser.uid;
        const currentEmail = auth.currentUser.email || '';
        const currentName = auth.currentUser.displayName || 'User';

        setIsProcessing(true);
        const res = await loadRazorpay();

        if (!res) {
            alert('Razorpay failed to load. Are you offline or using a strict adblocker?');
            setIsProcessing(false);
            return;
        }

        try {
            const orderResponse = await fetch('/.netlify/functions/createRazorpayOrder', { 
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    uid: currentUid,
                    email: currentEmail,
                    currency: currency,
                    productType: `section_${sectionId}`,
                    referredBy: localStorage.getItem('versant_affiliate_ref') || null,
                    gaClientId: getGAClientId()
                })
            });
            if (!orderResponse.ok) {
                throw new Error("Failed to generate secure Razorpay order. " + orderResponse.statusText);
            }
            const orderData = await orderResponse.json();

            const options = {
                key: process.env.REACT_APP_RAZORPAY_KEY_ID,
                amount: orderData.amount,
                currency: orderData.currency,
                name: "VersantPro Mastery",
                description: `Unlock 20 Practice Sets for ${sectionName}`,
                image: "https://versantpro.com/logo.png",
                order_id: orderData.order_id,
                handler: async function (response) {
                    console.log("Payment Success! Server verification starting...");
                    setIsProcessing(true);
                    
                    try {
                        const verifyRes = await fetch('/.netlify/functions/verifyRazorpayPayment', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                razorpay_order_id: response.razorpay_order_id,
                                razorpay_payment_id: response.razorpay_payment_id,
                                razorpay_signature: response.razorpay_signature,
                                uid: currentUid,
                                productType: `section_${sectionId}`,
                                referredBy: localStorage.getItem('versant_affiliate_ref') || null
                            })
                        });

                        const verifyData = await verifyRes.json();

                        if (verifyRes.ok) {
                            trackGA4Event('razorpay_payment_success', {
                                transaction_id: response.razorpay_payment_id,
                                payment_amount: 299,
                                currency: 'INR',
                                product_name: `VersantPro Section ${sectionName}`,
                                payment_method: 'razorpay'
                            });
                            trackFunnelEvent('payment_success', { value: 299, currency: 'INR', product_name: `VersantPro Section ${sectionName}`, transaction_id: response.razorpay_payment_id });
                            trackFunnelEvent('premium_activated', { product_name: `VersantPro Section ${sectionName}` });
                            
                            alert(`Payment Verified! You have successfully unlocked 20 practice sets for ${sectionName}.`);
                            navigate('/versant-practice-hub', { state: { selectedSection: sectionId } });
                        } else {
                            alert("Payment verification failed: " + verifyData.error);
                            setIsProcessing(false);
                            trackFunnelEvent('payment_failed', { product_name: `VersantPro Section ${sectionName}`, reason: verifyData.error || 'Verification failed' });
                        }
                    } catch (err) {
                        alert("Network error during verification. If money was deducted, contact support.");
                        console.error("Verification error:", err);
                        setIsProcessing(false);
                    }
                },
                prefill: {
                    name: currentName,
                    email: currentEmail,
                    contact: ""
                },
                theme: {
                    color: "#6366f1"
                },
                modal: {
                    ondismiss: function() {
                        trackGA4Event('razorpay_payment_failed', { product_name: `VersantPro Section ${sectionName}`, payment_amount: 299, currency: 'INR', reason: 'User dismissed checkout modal' });
                        trackFunnelEvent('payment_failed', { product_name: `VersantPro Section ${sectionName}`, reason: 'User dismissed checkout modal' });
                    }
                }
            };

            trackFunnelEvent('checkout_started', { product_name: `VersantPro Section ${sectionName}`, payment_amount: 299, currency: 'INR' });
            const paymentObject = new window.Razorpay(options);
            paymentObject.open();
        } catch (err) {
            console.error("Section Pack payment initialization error:", err);
            alert(`Could not initialize payment: ${err.message || 'Please check your connection and try again.'}`);
        }
        setIsProcessing(false);
    };

    return (
        <div className="min-h-screen gradient-bg text-white font-sans selection:bg-emerald-500/30">
            <style>{`
                @keyframes floatBubble {
                    0%, 100% { transform: translateY(0) scale(1); opacity: 0.2; }
                    50% { transform: translateY(-20px) scale(1.15); opacity: 0.6; }
                }
                @keyframes cardPopUp {
                    0% { transform: scale(0.92) translateY(24px); opacity: 0.5; }
                    60% { transform: scale(1.06) translateY(-20px); opacity: 1; }
                    100% { transform: scale(1.04) translateY(-14px); opacity: 1; }
                }
                .animate-bubble-1 { animation: floatBubble 6s ease-in-out infinite; }
                .animate-bubble-2 { animation: floatBubble 8s ease-in-out infinite; animation-delay: 2s; }
                .animate-bubble-3 { animation: floatBubble 7s ease-in-out infinite; animation-delay: 4s; }
                .animate-card-popup { animation: cardPopUp 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards; }
            `}</style>
            <Helmet>
                <title>Premium Versant Practice Plans | Upgrade to Pro</title>
                <meta name="description" content="Unlock 19 full-length Versant mock exams with advanced AI voice analysis. Prepare for your MNC interview with the ultimate test simulator." />
            </Helmet>

            <Header />

            <div className="relative pb-24">
                <div className="pt-24 pb-8 px-6 max-w-7xl mx-auto text-center relative z-10">
                {/* Hero Header */}
                <div className="max-w-3xl mx-auto mb-16 slide-up">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-900/40 border border-emerald-500/30 rounded-full text-emerald-300 text-xs font-bold uppercase tracking-widest mb-6 shadow-[0_0_15px_rgba(16,185,129,0.2)]">
                        <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
                        {pt.offerTag}
                    </div>
                    <h1 className="text-4xl md:text-6xl font-extrabold mb-6 tracking-tight">
                        {pt.mainTitle} <br/><span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-300 to-teal-100">{pt.mainSub}</span>
                    </h1>
                    <p className="text-lg text-emerald-100/80 font-medium">
                        {pt.mainDesc}
                    </p>
                </div>

                {/* Pricing Cards Container (4-Column Responsive Layout) */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto relative z-20 items-stretch">
                    
                    {/* Card 1: Free Plan */}
                    <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl p-6 flex flex-col items-start text-left transition-transform hover:-translate-y-2 duration-300">
                        <h3 className="text-xl font-bold mb-2 text-white">{pt.freeTitle}</h3>
                        <p className="text-emerald-100/60 mb-4 text-xs min-h-[36px]">{pt.freeDesc}</p>
                        <div className="mb-6">
                            <span className="text-4xl font-extrabold text-white">$0</span>
                            <span className="text-emerald-100/60 text-xs ml-1">Forever</span>
                        </div>
                        <ul className="space-y-3 mb-6 flex-1 w-full text-xs">
                            <li className="flex items-center gap-2 text-emerald-50">
                                <span className="text-emerald-400 font-bold">✓</span> Full Access to Test 1
                            </li>
                            <li className="flex items-center gap-2 text-emerald-50">
                                <span className="text-emerald-400 font-bold">✓</span> Basic AI Scoring
                            </li>
                            <li className="flex items-center gap-2 text-slate-500">
                                <span>✕</span> Premium Tests 2-20
                            </li>
                            <li className="flex items-center gap-2 text-slate-500">
                                <span>✕</span> Deep Pronunciation Analytics
                            </li>
                        </ul>
                        <button onClick={() => navigate('/signup')} className="w-full py-3 rounded-xl border border-white/20 hover:bg-white/10 text-xs font-bold transition-colors">
                            {pt.freeButton}
                        </button>
                    </div>

                    {/* Card 2: Section Packs */}
                    <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl p-6 flex flex-col items-start text-left transition-transform hover:-translate-y-2 duration-300">
                        <h3 className="text-xl font-bold mb-2 text-white">{pt.sectionTitle}</h3>
                        <p className="text-emerald-100/60 mb-4 text-xs min-h-[36px]">{pt.sectionDesc}</p>
                        <div className="mb-6">
                            <span className="text-4xl font-extrabold text-white">
                                {currency === 'INR' ? '₹299' : currency === 'EUR' ? '€2.99' : '$2.99'}
                            </span>
                            <span className="text-emerald-100/60 text-xs ml-1">
                                {currency === 'INR' ? '/ section' : `/ section`}
                            </span>
                        </div>
                        <ul className="space-y-3 mb-6 flex-1 w-full text-xs">
                            <li className="flex items-center gap-2 text-emerald-50">
                                <span className="text-emerald-400 font-bold">✓</span> 20 Drills for 1 Section
                            </li>
                            <li className="flex items-center gap-2 text-emerald-50">
                                <span className="text-emerald-400 font-bold">✓</span> Advanced AI Voice Analytics
                            </li>
                            <li className="flex items-center gap-2 text-emerald-50">
                                <span className="text-emerald-400 font-bold">✓</span> Model Answers & Outlines
                            </li>
                            <li className="flex items-center gap-2 text-emerald-50">
                                <span className="text-emerald-400 font-bold">✓</span> Lifetime Access for Section
                            </li>
                        </ul>
                        <button 
                            onClick={() => document.getElementById('section-wise-mastery')?.scrollIntoView({ behavior: 'smooth' })}
                            className="w-full py-3 rounded-xl border border-emerald-500/50 hover:bg-emerald-500/10 text-emerald-300 text-xs font-bold transition-all"
                        >
                            {pt.sectionButton}
                        </button>
                    </div>

                    {/* Card 3: 45-Day Sprint Pass (🎯 BEST FOR UPCOMING TEST) */}
                    <div className="bg-gradient-to-b from-teal-900/95 to-slate-900/95 backdrop-blur-xl border-2 border-teal-400 rounded-3xl p-6 flex flex-col items-start text-left relative transition-all duration-300 shadow-[0_0_50px_rgba(20,184,166,0.5)] transform md:-translate-y-3.5 scale-[1.04] animate-card-popup ring-4 ring-teal-400/20 z-30">
                        <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-gradient-to-r from-amber-400 via-teal-300 to-amber-400 text-slate-950 font-black px-4 py-1 rounded-full text-[11px] tracking-wider shadow-[0_0_20px_rgba(251,191,36,0.6)] uppercase animate-pulse whitespace-nowrap">
                            🎯 BEST FOR UPCOMING TEST
                        </div>
                        
                        <h3 className="text-xl font-extrabold mb-1 text-white">45-Day Sprint Pass</h3>
                        <p className="text-teal-200/80 mb-4 text-xs min-h-[36px]">Best for candidates with an upcoming Versant test in the next 1–6 weeks.</p>
                        
                        <div className="mb-4 flex items-baseline gap-2">
                            <span className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-teal-200 to-white">
                                {currency === 'INR' ? '₹699' : currency === 'EUR' ? '€6.99' : '$6.99'}
                            </span>
                            <span className="text-teal-100/60 text-xs uppercase tracking-wider">{currency}</span>
                        </div>
                        <div className="bg-amber-400/10 text-amber-300 text-[10px] font-bold px-2.5 py-1 rounded-full inline-block mb-4 border border-amber-400/20">
                            45 DAYS FULL ACCESS — NO RENEWAL
                        </div>

                        <ul className="space-y-3 mb-6 flex-1 w-full text-xs">
                            <li className="flex items-start gap-2">
                                <span className="text-teal-400 font-bold">✓</span>
                                <span className="text-teal-50 font-medium">All 38 Premium Mock Exams (19 R&W + 19 Speaking)</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-teal-400 font-bold">✓</span>
                                <span className="text-teal-50 font-medium">Unlimited AI Voice Evaluation for 45 Days</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-teal-400 font-bold">✓</span>
                                <span className="text-teal-50 font-medium">Deep Pronunciation & Grammar Corrections</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-teal-400 font-bold">✓</span>
                                <span className="text-teal-50 font-medium">Model Perfect Answers for every question</span>
                            </li>
                            <li className="flex items-start gap-2 text-teal-200/80">
                                <span className="text-amber-400 font-bold">⏱</span>
                                <span>Access expires automatically in 45 Days</span>
                            </li>
                        </ul>

                        <button 
                            onClick={handle45DayUpgrade} 
                            disabled={isProcessing} 
                            className="w-full py-3.5 rounded-xl font-extrabold text-sm shadow-[0_0_20px_rgba(20,184,166,0.4)] transition-all hover:scale-[1.02] bg-gradient-to-r from-teal-400 to-emerald-400 text-slate-950 disabled:opacity-50"
                        >
                            {isProcessing ? "Connecting Gateway..." : `Get 45-Day Pass — ${currency === 'INR' ? '₹699' : currency === 'EUR' ? '€6.99' : '$6.99'}`}
                        </button>
                    </div>

                    {/* Card 4: Lifetime VIP Pass (⭐ BEST VALUE (LIFETIME)) */}
                    <div className="bg-gradient-to-b from-emerald-900/95 to-slate-900/95 backdrop-blur-xl border-2 border-emerald-400 rounded-3xl p-6 flex flex-col items-start text-left relative transition-transform hover:-translate-y-2 duration-300 shadow-[0_0_45px_rgba(16,185,129,0.4)]">
                        <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-gradient-to-r from-emerald-400 via-teal-300 to-emerald-400 text-slate-950 font-black px-4 py-1 rounded-full text-[11px] tracking-wider shadow-[0_0_15px_rgba(16,185,129,0.5)] uppercase whitespace-nowrap">
                            ⭐ BEST VALUE (LIFETIME)
                        </div>
                        
                        <h3 className="text-xl font-extrabold mb-1 text-white">Lifetime VIP Pass</h3>
                        <p className="text-emerald-200/80 mb-3 text-xs min-h-[36px]">Everything you need to pass Versant English Tests with zero time limits.</p>
                        
                        <div className="mb-2 flex items-baseline gap-2">
                            <span className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-emerald-200 to-white">
                                {currency === 'INR' ? '₹1,449' : currency === 'EUR' ? '€14.99' : '$14.99'}
                            </span>
                            <span className="text-emerald-100/60 text-xs uppercase tracking-wider">{currency}</span>
                        </div>

                        <div className="bg-emerald-400/15 text-emerald-300 text-[10px] font-extrabold px-2.5 py-1 rounded-full inline-block mb-3 border border-emerald-400/40">
                            One-time payment • No renewal • Never expires
                        </div>

                        {/* Value Comparison Callout */}
                        <div className="w-full bg-amber-400/15 border border-amber-400/40 rounded-xl p-2.5 mb-4 text-[11px] text-amber-200 font-extrabold text-center shadow-sm leading-snug">
                            💡 Only {currency === 'INR' ? '₹750' : currency === 'EUR' ? '€8.00' : '$8.00'} more for Lifetime Access + eBook + Future Updates.
                        </div>

                        <ul className="space-y-3 mb-6 flex-1 w-full text-xs">
                            <li className="flex items-start gap-2">
                                <span className="text-emerald-400 font-bold">✓</span>
                                <span className="text-emerald-50 font-medium">All 38 Premium Mock Exams (19 R&W + 19 Speaking)</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-emerald-400 font-bold">✓</span>
                                <span className="text-emerald-50 font-medium">Unlimited AI Voice Evaluation & Scoring Forever</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-emerald-400 font-bold">✓</span>
                                <span className="text-emerald-50 font-medium">Deep Pronunciation & Grammar Corrections</span>
                            </li>
                            <li className="flex items-start gap-2 text-amber-300 font-bold">
                                <span className="text-amber-400 text-sm">📘</span>
                                <span>FREE Versant Mastery eBook Included (₹199 Value)</span>
                            </li>
                            <li className="flex items-start gap-2 text-emerald-300 font-bold">
                                <span className="text-emerald-400 text-sm">🛡️</span>
                                <span>7-Day 100% Money-Back Guarantee</span>
                            </li>
                            <li className="flex items-start gap-2 text-teal-200 font-semibold">
                                <span className="text-teal-300 font-bold">🚀</span>
                                <span>All Future Tests & Platform Updates Included Free</span>
                            </li>
                        </ul>

                        <button 
                            onClick={handleUpgrade} 
                            disabled={isProcessing} 
                            className="w-full py-3.5 rounded-xl font-extrabold text-sm shadow-[0_0_25px_rgba(16,185,129,0.4)] transition-all hover:scale-[1.02] bg-gradient-to-r from-emerald-400 to-teal-300 text-slate-950 disabled:opacity-50"
                        >
                            {isProcessing ? "Connecting Gateway..." : `Upgrade Lifetime VIP — ${currency === 'INR' ? '₹1449' : currency === 'EUR' ? '€14.99' : '$14.99'}`}
                        </button>
                    </div>
                </div>
            </div>

            {/* Melting Liquid Drips Divider */}
            <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-[0] translate-y-[1px] z-20 pointer-events-none">
                <svg viewBox="0 0 1440 200" preserveAspectRatio="none" className="relative block w-full h-[60px] md:h-[85px] fill-white text-white">
                    <path d="M0,0 C100,0 120,40 140,80 C155,110 170,120 185,120 C200,120 215,110 230,80 C250,40 270,0 370,0 C470,0 490,50 510,100 C525,135 540,150 555,150 C570,150 585,135 600,100 C620,50 640,0 740,0 C840,0 860,35 880,70 C895,95 910,110 925,110 C940,110 955,95 970,70 C990,35 1010,0 1110,0 C1210,0 1230,45 1250,90 C1265,125 1280,140 1295,140 C1310,140 1325,125 1340,90 C1360,45 1380,0 1440,0 L1440,200 L0,200 Z"></path>
                </svg>
            </div>
        </div>

             {/* SECTION 2: TARGET YOUR WEAKEST SECTIONS (SOLID WHITE BG) */}
            <div id="section-wise-mastery" className="bg-white py-24 text-slate-900 relative z-10">
                <div className="max-w-7xl mx-auto px-6 text-center">
                    <div className="max-w-3xl mx-auto mb-16">
                        <h2 className="text-3xl md:text-5xl font-black text-slate-900 mb-4 tracking-tight">
                            {pt.practiceTitle}
                        </h2>
                        <p className="text-slate-600 max-w-2xl mx-auto text-base font-medium">
                            {pt.practiceSubtitle}
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 text-left">
                        {getLocalizedPacks().map((pack) => (
                            <div key={pack.id} className="group relative bg-[#0f766e] border border-teal-600 rounded-[1.5rem] p-6 flex flex-col justify-between h-[290px] hover:shadow-[0_20px_50px_rgba(15,118,110,0.3)] hover:-translate-y-2 transition-all duration-500 overflow-hidden">
                                {/* Premium Glassmorphic reflection shine */}
                                <div className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/5 to-white/15 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"></div>

                                {/* Modern Feature Badge */}
                                <span className="absolute top-5 right-6 px-2.5 py-0.5 rounded-full bg-white/20 border border-white/10 text-white text-[9px] font-extrabold tracking-wider uppercase">
                                    20 Drills
                                </span>

                                <div className="relative z-10">
                                    {/* Glassmorphic Icon Circle */}
                                    <div className="w-12 h-12 rounded-xl bg-white/20 text-white flex items-center justify-center text-2xl mb-4 shadow-[inset_0_1px_1px_rgba(255,255,255,0.2)]">
                                        {pack.icon}
                                    </div>
                                    <h3 className="text-lg font-bold text-white mb-1.5 tracking-tight">{pack.name}</h3>
                                    <p className="text-teal-100/90 text-[13px] leading-relaxed mb-4 font-light">{pack.desc}</p>
                                </div>
                                <div className="border-t border-white/10 pt-3.5 flex items-center justify-between relative z-10">
                                    <div>
                                        <span className="text-xl font-black text-white">
                                            {currency === 'INR' ? '₹299' : currency === 'EUR' ? '€2.99' : '$2.99'}
                                        </span>
                                        <span className="text-[9px] text-white/70 block font-medium">{pt.onetime}</span>
                                    </div>
                                    <button 
                                        onClick={() => handleSectionUpgrade(pack.id, pack.name)}
                                        disabled={isProcessing}
                                        className="px-4 py-2.5 bg-white hover:bg-teal-50 text-[#0f766e] font-extrabold text-xs rounded-xl shadow-lg hover:shadow-white/20 active:scale-95 hover:-translate-y-[1.5px] transition-all duration-300 disabled:opacity-50"
                                    >
                                        {pt.freeButton === 'Plan Actual' ? 'Desbloquear' : pt.freeButton === '現在のプラン' ? '有効にする' : 'Unlock Pack'}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* SECTION 3: TRUST BADGES (DARK GREEN MATCHING BG) */}
            <div className="border-t border-white/10 py-20 text-center relative z-10">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="flex flex-wrap justify-center items-center gap-6 mb-10">
                        <div className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full text-sm text-slate-200">
                            <span>🔒</span> <span className="font-semibold">{pt.secured}</span>
                        </div>
                        <div className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full text-sm text-slate-200">
                            <span>⭐</span> <span className="font-semibold">{pt.rating}</span>
                        </div>
                        <div className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full text-sm text-slate-200">
                            <span>♾️</span> <span className="font-semibold">{pt.lifetimeAccess}</span>
                        </div>
                        <div className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full text-sm text-slate-200">
                            <span>🔁</span> <span className="font-semibold">{pt.refund}</span>
                        </div>
                    </div>
                    <p className="text-white/60 font-bold tracking-widest uppercase text-sm mb-8">{pt.trusted}</p>
                    <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16 opacity-90 transition-all duration-500 text-slate-200">
                        <div className="text-2xl font-black font-serif">Amazon</div>
                        <div className="text-2xl font-black tracking-tighter">Deloitte.</div>
                        <div className="text-2xl font-bold tracking-wider">Convergys</div>
                        <div className="text-2xl font-bold italic">Genpact</div>
                        <div className="text-xl font-bold">Tech Mahindra</div>
                    </div>
                </div>
            </div>

            {/* Ambient Background Effects */}
            <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
                <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-emerald-600/20 blur-[120px] rounded-full mix-blend-screen"></div>
                <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-teal-600/20 blur-[120px] rounded-full mix-blend-screen"></div>
            </div>
        </div>
    );
}
